import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FRAMEWORK_ROUTES } from '@framework/core';
import { get, post, isApiFailure } from '../http/client';
import { useAuth } from '../auth/AuthProvider';
import { SCREENS, goTo } from './routes';

interface ProfileDto {
  userId: string; email: string; username: string;
  firstName: string; lastName: string; name: string;
  roles: string[]; status: string; createdAt: number;
  editable: { name: string; label: string }[];
}

export function Profile({ onClose }: { onClose: () => void }) {
  const { session } = useAuth();

  const { data, isLoading } = useQuery<ProfileDto>({
    queryKey: ['profile'],
    queryFn: () => get<ProfileDto>(FRAMEWORK_ROUTES.profile),
    enabled: Boolean(session),
    retry: false,
  });

  if (!session) {
    return (
      <Page onClose={onClose}>
        <p className="flags-denied">Sign in to see your profile.</p>
        <button className="primary" onClick={() => goTo(SCREENS.login)}>Go to sign in</button>
      </Page>
    );
  }
  if (isLoading || !data) return <Page onClose={onClose}><p>Loading…</p></Page>;

  return (
    <Page onClose={onClose}>
      <section className="profile-block">
        <h2>Account</h2>
        <dl className="profile-grid">
          <dt>Name</dt><dd>{data.firstName} {data.lastName}</dd>
          <dt>Username</dt><dd>@{data.username}</dd>
          <dt>Email</dt><dd>{data.email}</dd>
          <dt>Status</dt><dd><span className={`status-chip status-${data.status}`}>{data.status}</span></dd>
          <dt>Member since</dt><dd>{new Date(data.createdAt).toLocaleDateString()}</dd>
        </dl>
      </section>

      <section className="profile-block">
        <h2>Roles</h2>
        {data.roles.length === 0
          ? <p className="ctl-hint">No roles yet. An administrator grants these.</p>
          : <div className="profile-roles">
              {data.roles.map((r) => <span key={r} className="role-pill">{r}</span>)}
            </div>}
      </section>

      <section className="profile-block">
        <h2>What you can edit</h2>
        {data.editable.length === 0 ? (
          <p className="ctl-hint">Nothing yet — your roles don't grant write access to any section.</p>
        ) : (
          <>
            <p className="ctl-hint">
              These are the sections showing a pencil for you. Derived from your roles, not stored.
            </p>
            <ul className="profile-sections">
              {data.editable.map((s) => (
                <li key={s.name}>{s.label} <code>{s.name}</code></li>
              ))}
            </ul>
          </>
        )}
      </section>

      <ChangePassword />
    </Page>
  );
}

/* -------------------------------------------------------------------------- */

function ChangePassword() {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNext] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const r = await post<{ message: string }>(FRAMEWORK_ROUTES.changePassword, { currentPassword, newPassword });
      setMsg({ ok: true, text: r.message });
      setCurrent(''); setNext('');
    } catch (err) {
      setMsg({ ok: false, text: isApiFailure(err) ? String(err.body.message ?? err.error) : 'Failed.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="profile-block">
      <h2>Change password</h2>
      <form onSubmit={submit} className="form profile-form">
        <label>Current password
          <input type="password" value={currentPassword} onChange={(e) => setCurrent(e.target.value)} required />
        </label>
        <label>New password
          <input type="password" value={newPassword} onChange={(e) => setNext(e.target.value)} required minLength={8} />
        </label>
        {msg && <p className={msg.ok ? 'notice-ok' : 'ctl-error'}>{msg.text}</p>}
        <button className="primary" disabled={busy}>{busy ? 'Saving…' : 'Change password'}</button>
      </form>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function Page({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="flags-page">
      <div className="container flags-inner">
        <header className="flags-head">
          <div>
            <h1>Your profile</h1>
            <p>Who you are, what you may edit, and your password.</p>
          </div>
          <button onClick={onClose}>← Back to site</button>
        </header>
        {children}
      </div>
    </div>
  );
}
